import classNames from 'classnames';
import React, { useState } from 'react';
import Button from '../components/Button';
import Caret from '../components/Caret';
import PopOver from '../components/deprecated/PopOver';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { TopicPostsOrder, TopicPostsType } from '../graphql/generated/graphql';
import allPostsIcon from '../icons/allPosts.svg';
import archiveIcon from '../icons/archive.svg';
import draftsIcon from '../icons/drafts.svg';
import postIcon from '../icons/post.svg';
import tocIcon from '../icons/tableOfContents.svg';
import templateIcon from '../icons/template.svg';
import verifiedIcon from '../icons/verified.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import styles from './TopicListDefaultsSettings.module.scss';
import type { EditTopicState } from './TopicView';

interface TopicListDefaultsSettingsProps {
  editTopicState: EditTopicState & { touched: boolean };
  updateEditTopicState: (payload: Partial<EditTopicState | null>) => void;
  postCounts: Record<TopicPostsType, number>;
}

const TopicListDefaultsSettings = ({
  editTopicState,
  updateEditTopicState,
  postCounts,
}: TopicListDefaultsSettingsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [show, setShow] = useState<TopicPostsType>(
    editTopicState.defaultShow ?? TopicPostsType.Published,
  );
  const [sort, setSort] = useState<TopicPostsOrder>(
    editTopicState.defaultSort ?? TopicPostsOrder.Popularity,
  );
  const isChanged =
    editTopicState.defaultShow !== show || editTopicState.defaultSort !== sort;

  const resetDefaults = () => {
    setSort(editTopicState.defaultSort ?? TopicPostsOrder.Popularity);
    setShow(editTopicState.defaultShow ?? TopicPostsType.Published);
  };

  return (
    <div className={styles.container}>
      {/* @ts-expect-error */}
      <PopOver
        open={isOpen}
        onOpenChange={(open) => {
          resetDefaults();
          setIsOpen(open);
        }}
        preventCloseOnContentClick
        popOver={
          <div className={styles.card}>
            <PopOverContainer noMargin noPadding width="auto">
              <div className={styles.menu}>
                <div className={styles.menuRow}>
                  Sort by
                  <PostFilterMenu
                    onChange={(value) => setSort(value)}
                    options={{
                      [TopicPostsOrder.Popularity]: { label: 'Popularity' },
                      [TopicPostsOrder.PublishedAt]: {
                        label: 'Recently published',
                      },
                      [TopicPostsOrder.UpdatedAt]: {
                        label: 'Recently updated',
                      },
                      [TopicPostsOrder.ViewedAt]: { label: 'Recently viewed' },
                      [TopicPostsOrder.Title]: { label: 'Alphabetical' },
                    }}
                    menuPlacement="bottom-end"
                    value={sort}
                    buttonProps={{
                      className: styles.menuButton,
                      variant: 'secondary',
                      size: 'small',
                    }}
                  />
                </div>
                <div className={styles.menuRow}>
                  <span>Show</span>
                  <PostFilterMenu<TopicPostsType>
                    onChange={(value) => setShow(value)}
                    options={{
                      [TopicPostsType.Published]: {
                        icon: postIcon,
                        label: 'Published',
                        count: postCounts.PUBLISHED,
                      },
                      [TopicPostsType.Verified]: {
                        icon: verifiedIcon,
                        label: 'Verified',
                        count: postCounts.VERIFIED,
                      },
                      [TopicPostsType.Template]: {
                        icon: templateIcon,
                        label: 'Template',
                        count: postCounts.TEMPLATE,
                      },
                      [TopicPostsType.Archived]: {
                        icon: archiveIcon,
                        label: 'Archived',
                        count: postCounts.ARCHIVED,
                      },
                      [TopicPostsType.Draft]: {
                        icon: draftsIcon,
                        label: 'Drafts',
                        count: postCounts.DRAFT,
                      },
                      [TopicPostsType.All]: {
                        icon: allPostsIcon,
                        label: 'All posts',
                        count: postCounts.ALL,
                      },
                    }}
                    showCountInLabel
                    value={show}
                    buttonProps={{
                      className: styles.menuButton,
                      variant: 'secondary',
                      size: 'small',
                    }}
                  />
                </div>
              </div>
              {isChanged && (
                <div className={styles.defaultsButton}>
                  <Button
                    variant="muted"
                    size="small"
                    onClick={() => {
                      resetDefaults();
                      setIsOpen(false);
                    }}
                  >
                    Reset defaults
                  </Button>
                  <Button
                    variant="link"
                    size="small"
                    onClick={() => {
                      updateEditTopicState({
                        defaultShow: show,
                        defaultSort: sort,
                      });
                      setIsOpen(false);
                    }}
                  >
                    Set as defaults
                  </Button>
                </div>
              )}
            </PopOverContainer>
          </div>
        }
        popOverPortal={document.body}
        trigger="click"
        placement="bottom-end"
      >
        <Button
          variant="secondary"
          size="small"
          icon={tocIcon}
          className={classNames(styles.openButton, { [styles.open]: isOpen })}
        >
          List defaults
          <span className={styles.caret}>
            <Caret direction={isOpen ? 'up' : 'down'} />
          </span>
        </Button>
      </PopOver>
    </div>
  );
};

export default TopicListDefaultsSettings;
